import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import useFlowStore from './store'
import { isTerminalAction } from './actions'
import FirmwareTimeoutField from './FirmwareTimeoutField'
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarCloseBtn,
  SidebarDeleteBtn,
  // v2 components
  SidebarTypeHeader,
  SidebarTypeLabel,
  SidebarNodeName,
  SidebarNameWrap,
  MsgBlock,
  MsgBlockTop,
  MsgNum,
  MsgTextarea,
  MsgBlockFooter,
  MsgCharCount,
  MsgRmBtn,
  MsgAddOptsBtn,
  OptionsSection,
  OptionsSectionHeader,
  OptionsSectionLabel,
  OptionRow,
  OptionNum,
  OptionInput,
  OptionRmBtn,
  ConnectionHint,
  FallbackCard,
  FallbackToggle,
  FallbackField,
  FallbackLabel,
  FallbackInput,
  SidebarFooterStats,
  SidebarStat,
  SidebarStatSep,
  SidebarSection,
  SidebarSectionHeader,
  SidebarSectionTitle,
  SidebarAddBtn,
  ActionSelect,
  ActionTagBadge,
  ActionVariablesCard,
  ActionVarTag,
} from './styles'

/* ── Character limit per message ── */
const MSG_LIMIT = 1000

/* ── WhatsApp option title limits (API constraint) ── */
const OPT_LIMIT_BUTTONS = 20
const OPT_LIMIT_LIST    = 24
const MAX_BUTTONS       = 3

/* ── Inline icons (no extra deps) ── */
const Icon = {
  close: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  plus: (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  trash: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  chevron: (up: boolean) => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <polyline points={up ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
    </svg>
  ),
  lightning: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
}

/* ════════════════════════════════════════════════════════════
   NodeSidebar v2 — unified step + optional action
════════════════════════════════════════════════════════════ */
/* Populates the form from node data — pure reusable function */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toForm = (n: any) => {
  const messages = n?.data.messages?.length ? n.data.messages : ['']
  const options  = n?.data.options || []
  // If there are options but optionsMessageIndex is null (legacy data), bind to the last message
  const optionsMessageIndex =
    n?.data.optionsMessageIndex != null
      ? n.data.optionsMessageIndex
      : options.length > 0
        ? messages.length - 1
        : null
  return {
    nodeId:              n?.data.nodeId          || '',
    name:                n?.data.name            || '',
    messages,
    options,
    optionsMessageIndex,
    action:              n?.data.action          ?? null,
    nextNodeId:          n?.data.nextNodeId      ?? null,
    fallbackMessage:     n?.data.fallbackMessage ?? null,
    fallbackNodeId:      n?.data.fallbackNodeId  ?? null,
    firmwareUpToDateNodeId: n?.data.firmwareUpToDateNodeId ?? null,
    firmwareTargetType:  n?.data.firmwareTargetType ?? null,
    firmwareTimeoutMinutes: n?.data.firmwareTimeoutMinutes ?? null,
    fallbackMaxRetries:  n?.data.fallbackMaxRetries ?? null,
  }
}

interface NodeSidebarProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  node: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  nodes: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  edges: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpdate: (nodeId: string, data: any) => void;
  onDelete: (nodeId: string) => void;
  onClose: () => void;
}

const CONSULT_MAC_VARIABLES = [
  '{companyName}',
  '{panelMac}',
  '{panelType}',
  '{firmVersion}',
  '{deviceModel}',
]

const UPDATE_FIRMWARE_VARIABLES = [
  '{newFirmVersion}',
]

const FIRMWARE_TARGET_OPTIONS = [
  { value: 'Citypanel', labelKey: 'node_sidebar.firmware_target_zigbee' },
  { value: 'Citypanel 4.0', labelKey: 'node_sidebar.firmware_target_fsk_ihorn' },
  { value: 'Data bus',  labelKey: 'node_sidebar.firmware_target_data_bus' },
]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function hasAncestorAction(nodeId: string, actionName: string, nodes: any[], edges: any[]): boolean {
  const visited = new Set<string>()
  const queue = [nodeId]

  while (queue.length > 0) {
    const current = queue.shift()!
    if (visited.has(current)) continue
    visited.add(current)

    const parentEdges = edges.filter((e: { target: string }) => e.target === current)
    for (const edge of parentEdges) {
      const parentNode = nodes.find((n: { id: string }) => n.id === edge.source)
      if (!parentNode) continue
      if (parentNode.data?.action === actionName) return true
      queue.push(parentNode.id)
    }
  }
  return false
}

export default function NodeSidebar({ node, nodes, edges, onUpdate, onDelete, onClose }: NodeSidebarProps) {
  const { t } = useTranslation()
  const setSelectedNode = useFlowStore((s) => s.setSelectedNode)

  /* Lazy initializer — ensures the form never starts as {} with undefined fields */
  const [form, setForm] = useState(() => toForm(node))
  const [showFallback, setShowFallback] = useState(!!(node?.data.fallbackMessage))
  const [showAction,   setShowAction]   = useState(!!(node?.data.action))

  /* Sync form when NODE CHANGES (node.id changes) */
  useEffect(() => {
    if (!node) return
    setForm(toForm(node))
    setShowFallback(!!(node.data.fallbackMessage))
    setShowAction(!!(node.data.action))
  }, [node?.id])

  if (!node) return null

  /* Propagation helpers */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const patch = (field: string, value: any) => {
    const next = { ...form, [field]: value }
    setForm(next)
    onUpdate(node.id, next)
  }

  /* Toggle action section on/off */
  const handleToggleAction = () => {
    if (showAction) {
      // Turning off — clear the action field
      const next = { ...form, action: null }
      setForm(next)
      onUpdate(node.id, next)
    }
    setShowAction(!showAction)
  }

  /* ── Checks if an option already has a connection on the canvas ── */
  const isOptionConnected = (optIdx: number) =>
    edges?.some((e) => e.source === node.id && e.sourceHandle === `opt-${optIdx}`)

  /* ── Messages ── */
  const updateMsg = (i: number, val: string) => {
    const msgs = [...form.messages]
    msgs[i] = val
    patch('messages', msgs)
  }

  const addMsg = () => patch('messages', [...form.messages, ''])

  const removeMsg = (i: number) => {
    const msgs = form.messages.filter((_: string, j: number) => j !== i)
    const next = { ...form, messages: msgs }
    // If the message that had options was removed, clear the options
    if (form.optionsMessageIndex === i) {
      next.options = []
      next.optionsMessageIndex = null
    } else if (form.optionsMessageIndex !== null && i < form.optionsMessageIndex) {
      next.optionsMessageIndex = form.optionsMessageIndex - 1
    }
    setForm(next)
    onUpdate(node.id, next)
  }

  const attachOptions = (msgIdx: number) => {
    const next = { ...form, options: [{ label: '', nextNodeId: '' }], optionsMessageIndex: msgIdx }
    setForm(next)
    onUpdate(node.id, next)
  }

  /* ── Options ── */
  const updateOpt = (i: number, val: string) => {
    const opts = form.options.map((o: { label: string; nextNodeId: string }, j: number) => j === i ? { ...o, label: val } : o)
    patch('options', opts)
  }

  const addOpt = () => patch('options', [...form.options, { label: '', nextNodeId: '' }])

  const removeOpt = (i: number) => {
    const opts = form.options.filter((_: unknown, j: number) => j !== i)
    const next = { ...form, options: opts }
    if (opts.length === 0) next.optionsMessageIndex = null
    setForm(next)
    onUpdate(node.id, next)
  }

  /* ── Derived ── */
  const hasAction  = Boolean(form.action)
  const hasOpts    = form.options?.length > 0
  const totalMsgs  = form.messages?.length || 0
  const totalOpts  = form.options?.length || 0
  const optLimit   = totalOpts > MAX_BUTTONS ? OPT_LIMIT_LIST : OPT_LIMIT_BUTTONS

  /* ── Type label — reflects presence of action ── */
  const visualType = hasAction ? 'action' : 'step'
  const typeLabel  = hasAction ? t('node_sidebar.type_action') : t('node_sidebar.type_step')

  const hasConsultMacAncestor = hasAncestorAction(node.id, 'consultMac', nodes, edges)

  const KNOWN_ACTIONS = ['consultMac', 'updateFirmware', 'transferToAgent']

  const ACTION_OPTIONS = [
    { value: '',                label: t('action_options.none'),              disabled: false },
    { value: 'consultMac',      label: t('action_options.consultMac'),        disabled: false },
    { value: 'updateFirmware',  label: t('action_options.updateFirmware'),    disabled: !hasConsultMacAncestor },
    { value: 'transferToAgent', label: t('action_options.transferToAgent'),   disabled: false },
  ]

  const inheritedVariables = !hasAction
    ? hasAncestorAction(node.id, 'consultMac', nodes, edges)
      ? CONSULT_MAC_VARIABLES
      : hasAncestorAction(node.id, 'updateFirmware', nodes, edges)
        ? UPDATE_FIRMWARE_VARIABLES
        : []
    : []

  return (
    <Sidebar>
      {/* ── Colored header — color driven by presence of action ── */}
      <SidebarTypeHeader $type={visualType}>
        <SidebarNameWrap>
          <SidebarTypeLabel $type={visualType}>{typeLabel}</SidebarTypeLabel>
          <SidebarNodeName
            value={form.name}
            onChange={(e) => patch('name', e.target.value)}
            placeholder={t('node_sidebar.name_placeholder', { type: typeLabel.toLowerCase() })}
          />
        </SidebarNameWrap>
        <SidebarCloseBtn
          onClick={() => { onClose(); setSelectedNode(null) }}
          style={{ marginLeft: 8, flexShrink: 0 }}
        >
          {Icon.close}
        </SidebarCloseBtn>
      </SidebarTypeHeader>

      {/* ── Stats bar ── */}
      <SidebarFooterStats>
        <SidebarStat><strong>{totalMsgs}</strong> {t(`node_sidebar.stat_message_${totalMsgs === 1 ? 'one' : 'other'}`)}</SidebarStat>
        {hasOpts && (
          <>
            <SidebarStatSep>·</SidebarStatSep>
            <SidebarStat><strong>{totalOpts}</strong> {t(`node_sidebar.stat_option_${totalOpts === 1 ? 'one' : 'other'}`)}</SidebarStat>
          </>
        )}
      </SidebarFooterStats>

      {/* ── Body ── */}
      <SidebarBody>

        {/* ══ Messages ══ */}
        <SidebarSection>
          <SidebarSectionHeader>
            <SidebarSectionTitle>{t('node_sidebar.messages_title')}</SidebarSectionTitle>
            <SidebarAddBtn onClick={addMsg}>{t('node_sidebar.add_message')}</SidebarAddBtn>
          </SidebarSectionHeader>

          {inheritedVariables.length > 0 && (
            <ActionVariablesCard style={{ marginBottom: 10 }}>
              <SidebarSectionTitle style={{ color: '#059669', marginBottom: 6, display: 'block' }}>
                {t('node_sidebar.action_variables_title')}
              </SidebarSectionTitle>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {inheritedVariables.map((v) => (
                  <ActionVarTag key={v}>{v}</ActionVarTag>
                ))}
              </div>
              <div style={{ marginTop: 6, fontSize: 10, color: '#6B7280', lineHeight: 1.4 }}>
                {t('node_sidebar.action_variables_hint')}
              </div>
            </ActionVariablesCard>
          )}

          {form.messages?.map((msg: string, i: number) => {
            const isOptsMsg      = form.optionsMessageIndex === i
            const charCount      = msg.length
            const showAddOptsBtn = !hasOpts

            return (
              <div key={i}>
                <MsgBlock>
                  <MsgBlockTop>
                    <MsgNum>{i + 1}</MsgNum>
                  </MsgBlockTop>
                  <MsgTextarea
                    value={msg}
                    onChange={(e) => updateMsg(i, e.target.value)}
                    placeholder={t('node_sidebar.msg_placeholder', { index: i + 1 })}
                    rows={3}
                  />
                  <MsgBlockFooter>
                    <MsgCharCount $warn={charCount > MSG_LIMIT * 0.9}>
                      {charCount > 0 ? `${charCount} / ${MSG_LIMIT}` : ''}
                    </MsgCharCount>
                    {form.messages.length > 1 && (
                      <MsgRmBtn onClick={() => removeMsg(i)}>{Icon.close}</MsgRmBtn>
                    )}
                  </MsgBlockFooter>
                </MsgBlock>

                {/* Button to add options to this message */}
                {showAddOptsBtn && (
                  <div style={{ marginBottom: 6 }}>
                    <MsgAddOptsBtn onClick={() => attachOptions(i)}>
                      {Icon.plus} {t('node_sidebar.attach_options')}
                    </MsgAddOptsBtn>
                  </div>
                )}

                {/* Options linked to this message */}
                {isOptsMsg && (
                  <OptionsSection>
                    <OptionsSectionHeader>
                      <OptionsSectionLabel>{t('node_sidebar.options_title')}</OptionsSectionLabel>
                      <SidebarAddBtn onClick={addOpt}>{t('node_sidebar.add_option')}</SidebarAddBtn>
                    </OptionsSectionHeader>

                    {form.options.map((opt: { label: string }, oi: number) => {
                      const connected = isOptionConnected(oi)
                      return (
                        <OptionRow key={oi}>
                          {/* Number with connection state */}
                          <OptionNum $connected={connected} title={connected ? t('node_sidebar.option_connected') : t('node_sidebar.option_drag_hint')}>
                            {oi + 1}
                          </OptionNum>

                          {/* Tooltip shown on number hover when not connected */}
                          {!connected && (
                            <ConnectionHint>{t('node_sidebar.drag_hint_canvas')}</ConnectionHint>
                          )}

                          {/* Option text */}
                          <OptionInput
                            value={opt.label}
                            maxLength={optLimit}
                            onChange={(e) => updateOpt(oi, e.target.value)}
                            placeholder={t('node_sidebar.option_placeholder', { index: oi + 1 })}
                          />
                          <MsgCharCount $warn={opt.label.length >= optLimit * 0.9} style={{ fontSize: 9, marginLeft: 4, whiteSpace: 'nowrap' }}>
                            {opt.label.length > 0 ? `${opt.label.length}/${optLimit}` : ''}
                          </MsgCharCount>

                          {/* Remove — visible only on hover via CSS */}
                          <OptionRmBtn className="opt-rm" onClick={() => removeOpt(oi)}>
                            {Icon.close}
                          </OptionRmBtn>
                        </OptionRow>
                      )
                    })}

                    {/* Fallback */}
                    <FallbackToggle onClick={() => setShowFallback(!showFallback)}>
                      {Icon.chevron(showFallback)} {t('node_sidebar.fallback_toggle')}
                    </FallbackToggle>

                    {showFallback && (
                      <FallbackCard>
                        <FallbackField>
                          <FallbackLabel>{t('node_sidebar.fallback_error_label')}</FallbackLabel>
                          <FallbackInput
                            value={form.fallbackMessage}
                            onChange={(e) => patch('fallbackMessage', e.target.value)}
                            placeholder={t('node_sidebar.fallback_error_placeholder')}
                          />
                        </FallbackField>
                        <FallbackField>
                          <FallbackLabel>{t('node_sidebar.fallback_retries_label')}</FallbackLabel>
                          <FallbackInput
                            type="number"
                            min="1"
                            max="10"
                            value={form.fallbackMaxRetries}
                            onChange={(e) => patch('fallbackMaxRetries', e.target.value ? Number(e.target.value) : '')}
                            placeholder="3"
                            style={{ maxWidth: 80 }}
                          />
                        </FallbackField>
                        <FallbackField>
                          <FallbackLabel>{t('node_sidebar.fallback_node_label')}</FallbackLabel>
                          <ActionSelect
                            value={form.fallbackNodeId ?? ''}
                            onChange={(e) => patch('fallbackNodeId', e.target.value || null)}
                          >
                            <option value="">{t('node_sidebar.fallback_node_none')}</option>
                            {nodes
                              .filter((n: { id: string }) => n.id !== node.id)
                              .map((n: { id: string; data: { name?: string; nodeId?: string } }) => (
                                <option key={n.id} value={n.data.nodeId ?? n.id}>
                                  {n.data.name || n.data.nodeId || n.id}
                                </option>
                              ))}
                          </ActionSelect>
                        </FallbackField>
                      </FallbackCard>
                    )}
                  </OptionsSection>
                )}
              </div>
            )
          })}
        </SidebarSection>

        {/* ══ Action — optional, collapsible ══ */}
        <SidebarSection>
          <SidebarSectionHeader>
            <SidebarSectionTitle style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {Icon.lightning}
              {t('node_sidebar.action_section_toggle')}
            </SidebarSectionTitle>
            <SidebarAddBtn
              onClick={handleToggleAction}
              style={showAction ? { borderColor: '#8B5CF6', color: '#8B5CF6' } : {}}
            >
              {showAction ? t('node_sidebar.action_section_off') : t('node_sidebar.action_section_on')}
            </SidebarAddBtn>
          </SidebarSectionHeader>

          {showAction && (
            <>
              <SidebarSectionHeader style={{ marginTop: 6 }}>
                <SidebarSectionTitle>{t('node_sidebar.action_type_title')}</SidebarSectionTitle>
              </SidebarSectionHeader>
              <ActionSelect
                value={KNOWN_ACTIONS.includes(form.action) ? form.action : (form.action ? 'custom' : '')}
                onChange={(e) => patch('action', e.target.value === 'custom' ? '' : e.target.value)}
              >
                {ACTION_OPTIONS.map((a) => (
                  <option key={a.value} value={a.value} disabled={a.disabled}>
                    {a.disabled
                      ? `${a.label} (${t('action_options.requires_consult_mac')})`
                      : a.label}
                  </option>
                ))}
              </ActionSelect>

              {form.action === 'updateFirmware' && (
                <>
                  <SidebarSectionHeader style={{ marginTop: 6 }}>
                    <SidebarSectionTitle>{t('node_sidebar.firmware_target_title')}</SidebarSectionTitle>
                  </SidebarSectionHeader>
                  <ActionSelect
                    value={form.firmwareTargetType ?? ''}
                    onChange={(e) => patch('firmwareTargetType', e.target.value || null)}
                  >
                    <option value="" disabled>{t('node_sidebar.firmware_target_placeholder')}</option>
                    {FIRMWARE_TARGET_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
                    ))}
                  </ActionSelect>
                </>
              )}

              {form.action && !KNOWN_ACTIONS.includes(form.action) && (
                <>
                  <ActionTagBadge>{t('node_sidebar.action_custom_tag')}</ActionTagBadge>
                  <FallbackInput
                    style={{ marginTop: 6 }}
                    value={form.action}
                    onChange={(e) => patch('action', e.target.value)}
                    placeholder="tag_da_acao"
                  />
                </>
              )}

              {form.action && !isTerminalAction(form.action) && (
                <>
                  <FallbackToggle onClick={() => setShowFallback(!showFallback)} style={{ marginTop: 8 }}>
                    {Icon.chevron(showFallback)} {t('node_sidebar.fallback_toggle')}
                  </FallbackToggle>

                  {showFallback && (
                    <FallbackCard>
                      <FallbackField>
                        <FallbackLabel>
                          {form.action === 'updateFirmware'
                            ? t('node_sidebar.fallback_firmware_message_label')
                            : t('node_sidebar.fallback_error_label')}
                        </FallbackLabel>
                        <FallbackInput
                          value={form.fallbackMessage}
                          onChange={(e) => patch('fallbackMessage', e.target.value)}
                          placeholder={
                            form.action === 'updateFirmware'
                              ? t('node_sidebar.fallback_firmware_message_placeholder')
                              : t('node_sidebar.fallback_error_placeholder')
                          }
                        />
                      </FallbackField>
                      {form.action === 'updateFirmware' ? (
                        <FirmwareTimeoutField
                          value={form.firmwareTimeoutMinutes}
                          onChange={(minutes) => patch('firmwareTimeoutMinutes', minutes)}
                        />
                      ) : (
                        <FallbackField>
                          <FallbackLabel>{t('node_sidebar.fallback_retries_label')}</FallbackLabel>
                          <FallbackInput
                            type="number"
                            min="1"
                            max="10"
                            value={form.fallbackMaxRetries}
                            onChange={(e) => patch('fallbackMaxRetries', e.target.value ? Number(e.target.value) : '')}
                            placeholder="1"
                            style={{ maxWidth: 80 }}
                          />
                        </FallbackField>
                      )}
                      <FallbackField>
                        <FallbackLabel>{t('node_sidebar.fallback_node_label')}</FallbackLabel>
                        <ActionSelect
                          value={form.fallbackNodeId ?? ''}
                          onChange={(e) => patch('fallbackNodeId', e.target.value || null)}
                        >
                          <option value="">{t('node_sidebar.fallback_node_none')}</option>
                          {nodes
                            .filter((n: { id: string }) => n.id !== node.id)
                            .map((n: { id: string; data: { name?: string; nodeId?: string } }) => (
                              <option key={n.id} value={n.data.nodeId ?? n.id}>
                                {n.data.name || n.data.nodeId || n.id}
                              </option>
                            ))}
                        </ActionSelect>
                      </FallbackField>
                      {form.action === 'updateFirmware' && (
                        <FallbackField>
                          <FallbackLabel>{t('node_sidebar.firmware_up_to_date_node_label')}</FallbackLabel>
                          <ActionSelect
                            value={form.firmwareUpToDateNodeId ?? ''}
                            onChange={(e) => patch('firmwareUpToDateNodeId', e.target.value || null)}
                          >
                            <option value="">{t('node_sidebar.fallback_node_none')}</option>
                            {nodes
                              .filter((n: { id: string }) => n.id !== node.id)
                              .map((n: { id: string; data: { name?: string; nodeId?: string } }) => (
                                <option key={n.id} value={n.data.nodeId ?? n.id}>
                                  {n.data.name || n.data.nodeId || n.id}
                                </option>
                              ))}
                          </ActionSelect>
                        </FallbackField>
                      )}
                    </FallbackCard>
                  )}
                </>
              )}
            </>
          )}
        </SidebarSection>

      </SidebarBody>

      {/* ── Footer ── */}
      <SidebarFooter>
        <SidebarDeleteBtn onClick={() => onDelete(node.id)}>
          {Icon.trash} {t('node_sidebar.delete_node')}
        </SidebarDeleteBtn>
      </SidebarFooter>
    </Sidebar>
  )
}
